/**
 * Turns the review date rendered by the theme into relative time ("2 hours ago") for reviews under 7 days old.
 * One shared timer updates every instance without changing the review data itself.
 */
if (!customElements.get('review-relative-date')) {
  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  customElements.define(
    'review-relative-date',
    class ReviewRelativeDate extends HTMLElement {
      connectedCallback() {
        this.render();
      }

      getZonedParts(date, timeZone) {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(date);

        return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
      }

      getReviewDate() {
        const dateMatch = this.getAttribute('date')?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const timeMatch = this.getAttribute('time')?.match(/^(\d{1,2}):(\d{2})$/);
        const timeZone = this.getAttribute('time-zone');

        if (!dateMatch || !timeMatch || !timeZone) return new Date(NaN);

        const desiredTimestamp = Date.UTC(
          Number(dateMatch[1]),
          Number(dateMatch[2]) - 1,
          Number(dateMatch[3]),
          Number(timeMatch[1]),
          Number(timeMatch[2])
        );
        let timestamp = desiredTimestamp;

        try {
          // Iterate because the applicable UTC offset can change around daylight-saving transitions.
          for (let attempt = 0; attempt < 3; attempt += 1) {
            const parts = this.getZonedParts(new Date(timestamp), timeZone);
            const renderedTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
            const correction = renderedTimestamp - desiredTimestamp;

            if (correction === 0) break;
            timestamp -= correction;
          }
        } catch (_error) {
          return new Date(NaN);
        }

        return new Date(timestamp);
      }

      render() {
        const timeElement = this.querySelector('time');
        const reviewDate = this.getReviewDate();

        if (!timeElement || Number.isNaN(reviewDate.getTime())) return;

        const locale = document.documentElement.lang || navigator.language || 'en';
        const elapsed = Date.now() - reviewDate.getTime();
        const absoluteElapsed = Math.abs(elapsed);
        const direction = elapsed >= 0 ? -1 : 1;

        timeElement.dateTime = reviewDate.toISOString();

        if (absoluteElapsed < HOUR) {
          const minutes = Math.max(1, Math.floor(absoluteElapsed / MINUTE));
          timeElement.textContent = new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(direction * minutes, 'minute');
        } else if (absoluteElapsed < DAY) {
          const hours = Math.max(1, Math.floor(absoluteElapsed / HOUR));
          timeElement.textContent = new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(direction * hours, 'hour');
        } else if (absoluteElapsed < 7 * DAY) {
          const days = Math.max(1, Math.floor(absoluteElapsed / DAY));
          timeElement.textContent = new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(direction * days, 'day');
        } else {
          timeElement.textContent = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(reviewDate);
        }
      }
    }
  );

  window.setInterval(() => {
    document.querySelectorAll('review-relative-date').forEach((reviewDate) => reviewDate.render());
  }, MINUTE);
}
